/**
 * Shared domain-filter helpers for the two Relevance Engine UI surfaces
 * (plain RE database mode and A/B classifications mode).
 *
 * Both UIs accept a free-form textarea (one domain per line or comma-separated)
 * and produce a sanitized, deduplicated list of domains capped at
 * MAX_DOMAIN_FILTER. See bd deepcontext-o4fo4 for the alignment decision.
 */

/** Hard cap on the number of domains the user can submit in a single request. */
export const MAX_DOMAIN_FILTER = 50;

/**
 * Strip protocol, www., path, port, and query string from a single domain entry,
 * then lowercase and trim. Returns the canonical bare-host form.
 */
export const sanitizeDomain = (domain: string): string => {
    let cleaned = domain.trim().toLowerCase();
    // Remove protocol (http://, https://)
    cleaned = cleaned.replace(/^https?:\/\//, '');
    // Remove www.
    cleaned = cleaned.replace(/^www\./, '');
    // Remove trailing slashes and paths
    cleaned = cleaned.split('/')[0];
    // Remove port numbers
    cleaned = cleaned.split(':')[0];
    // Remove query strings
    cleaned = cleaned.split('?')[0];
    return cleaned;
};

/**
 * Split the raw textarea input on newlines/commas, sanitize each entry, and
 * keep only entries that contain at least one dot (a minimal validity check).
 * Duplicates are preserved by callers — pass through Array.from(new Set(...))
 * if you need uniqueness.
 */
export const parseDomainFilter = (input: string): string[] => {
    return input
        .split(/[\n,]/)
        .map(d => sanitizeDomain(d))
        .filter(d => d.length > 0 && d.includes('.'));
};
